import { _ as _extends } from '../_rollupPluginBabelHelpers-e8fb2e5c.js';
import React__default, { useState, useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { c as classnames } from '../index-6ff23041.js';
import { _ as _default } from '../Popover-f4d1cac0.js';
import { p as positions } from '../configs-fed6ac34.js';
import { n as noop } from '../index-a0e4e333.js';
import useDeviceType from '../hooks/useDeviceType.js';
import { GeneUIDesignSystemContext } from '../GeneUIProvider/index.js';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import '../_commonjsHelpers-24198af3.js';
import 'react-dom';
import '../dateValidation-67caec66.js';
import '../hooks/useWindowSize.js';

var css_248z = "[data-gene-ui-version=\"2.13.0\"] .react-tiny-popover-container{z-index:500}[data-gene-ui-version=\"2.13.0\"] .tooltip-c-p{pointer-events:none}[data-gene-ui-version=\"2.13.0\"] .s-small .tooltip-c-p{padding:1.5rem}[data-gene-ui-version=\"2.13.0\"] .tooltip-content{background:rgba(var(--background-sc-rgb),.6);border-radius:.4rem;color:rgba(var(--background-rgb),.7);font:600 1.2rem/1.6rem var(--font-family);max-width:20rem;padding:.6rem 1rem;word-break:break-word}[data-gene-ui-version=\"2.13.0\"] .tooltip-content>*+*{margin:.2rem 0 0}[data-gene-ui-version=\"2.13.0\"] .tooltip-content .tooltip-title{color:var(--background)}[data-gene-ui-version=\"2.13.0\"] .s-small .tooltip-content{font:600 1rem/1.4rem var(--font-family);padding:.6rem .8rem}";
styleInject(css_248z);

function Tooltip(_ref) {
  let {
    children,
    position,
    size,
    style,
    text,
    title,
    transitionDuration,
    customPosition,
    alwaysShow,
    disableReposition,
    onClick,
    padding,
    screenType,
    isVisible,
    ...props
  } = _ref;
  const {
    isMobile
  } = useDeviceType(screenType);
  const [isPopoverOpen, setPopoverState] = useState(false);
  const {
    geneUIProviderRef
  } = useContext(GeneUIDesignSystemContext);
  const mouseEnterHandler = useCallback(() => !alwaysShow && setPopoverState(true), [alwaysShow]);
  const mouseLeaveHandler = useCallback(() => !alwaysShow && setPopoverState(false), [alwaysShow]);
  useEffect(() => {
    if (children.props.disabled) {
      mouseLeaveHandler();
    }
  }, [children.props.disabled]);
  const preferredPosition = position === 'auto' ? positions : position;
  const checkNudged = useCallback(_ref2 => {
    let {
      nudgedLeft,
      nudgedTop
    } = _ref2;
    return isMobile ? !(nudgedTop || nudgedLeft) : true;
  }, [isMobile]);
  return isVisible ? /*#__PURE__*/React__default.createElement(_default, {
    contentDestination: geneUIProviderRef.current,
    transitionDuration: transitionDuration,
    disableReposition: disableReposition,
    isOpen: alwaysShow || isPopoverOpen,
    position: preferredPosition,
    contentLocation: customPosition,
    align: "center",
    padding: padding,
    content: rect => checkNudged(rect) && /*#__PURE__*/React__default.createElement("div", _extends({
      className: classnames('tooltip-c-p', "s-".concat(size)),
      style: {
        ...style
      }
    }, props), (title || text) && /*#__PURE__*/React__default.createElement("div", {
      className: "tooltip-content"
    }, title && /*#__PURE__*/React__default.createElement("div", {
      className: "tooltip-title"
    }, title), text && /*#__PURE__*/React__default.createElement("div", {
      className: "tooltip-text"
    }, text)))
  }, children ? /*#__PURE__*/React__default.cloneElement(children, {
    onMouseEnter: mouseEnterHandler,
    onMouseLeave: mouseLeaveHandler,
    onClick: e => {
      const {
        onClick: onClickHandler
      } = children.props;
      typeof onClickHandler === 'function' && onClickHandler(e);
      onClick(e);
    }
  }) : null) : children;
}
Tooltip.propTypes = {
  /**
   * Different sizes for 'Tooltip'.
   */
  size: PropTypes.oneOf(['default', 'small']),
  /**
   * Text for 'Tooltip'.
   */
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Title for 'Tooltip'.
   */
  title: PropTypes.string,
  /**
   * Style object, to have extra styles.
   */
  style: PropTypes.object,
  /**
   * Have always visible 'Tooltip'.
   */
  alwaysShow: PropTypes.bool,
  /**
   * Custom positions(left, top) for 'Tooltip'.
   */
  customPosition: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number
  }),
  /**
   * Duration for showing 'Tooltip'.
   */
  transitionDuration: PropTypes.number,
  /**
   * Any valid React node.
   */
  children: PropTypes.node,
  /**
   * Disable/Enable repositions.
   */
  disableReposition: PropTypes.bool,
  /**
   * 'Tooltip' position to be displayed
   */
  position: PropTypes.oneOf(['auto', ...positions]),
  /**
   * 'Tooltip' padding from the target element
   */
  padding: PropTypes.number,
  /**
   * If isVisible is false, the component will render only children without a tooltip wrapped.
   */
  isVisible: PropTypes.bool
};
Tooltip.defaultProps = {
  position: 'auto',
  size: 'default',
  transitionDuration: 0,
  disableReposition: false,
  onClick: noop,
  padding: 5,
  screenType: 'desktop',
  isVisible: true
};

export { Tooltip as default };
